<template>
  <!-- Patient Profile's disabled-button card, same content: an upsell for a feature the office
       hasn't paid for, or the reasons this patient can't get it right now. -->
  <div v-if="why && why.upsell" class="aw aw--upsell" data-test="pp-why-upsell">
    <div class="aw__icon" aria-hidden="true"><i :class="`fa ${icon}`"></i></div>
    <h6 class="aw__title">{{ why.upsell.title }}</h6>
    <p class="aw__desc">{{ why.upsell.desc }}</p>
    <!--
      One button per feature, with the key written out: the repo's "Request to Enable" gate
      (cypress/node-tests/feature-request-paid-only-guardrail.test.js) reads keys literally, and lists
      this card as the surface that offers each of them.
    -->
    <FeatureRequestButton v-if="feature === 'FORMS'" class="aw__cta" feature-key="FORMS" :feature-label="why.upsell.featureLabel" :source-page="sourcePage" />
    <FeatureRequestButton v-else-if="feature === 'SMS'" class="aw__cta" feature-key="SMS" :feature-label="why.upsell.featureLabel" :source-page="sourcePage" />
    <FeatureRequestButton v-else-if="feature === 'EMAIL'" class="aw__cta" feature-key="EMAIL" :feature-label="why.upsell.featureLabel" :source-page="sourcePage" />
    <FeatureRequestButton v-else-if="feature === 'TX_PRESENTER'" class="aw__cta" feature-key="TX_PRESENTER" :feature-label="why.upsell.featureLabel" :source-page="sourcePage" />
    <FeatureRequestButton v-else-if="feature === 'REVIEWS'" class="aw__cta" feature-key="REVIEWS" :feature-label="why.upsell.featureLabel" :source-page="sourcePage" />
    <FeatureRequestButton v-else-if="feature === 'ALL_IN_ONE'" class="aw__cta" feature-key="ALL_IN_ONE" :feature-label="why.upsell.featureLabel" :source-page="sourcePage" />
    <FeatureRequestNote v-if="feature" :feature="feature" />
  </div>

  <div v-else-if="why && why.unavailable" class="aw aw--unavailable" data-test="pp-why-unavailable">
    <div class="aw__head">
      <i class="fa fa-info-circle" aria-hidden="true"></i>
      <span>{{ why.unavailable.title }}</span>
    </div>
    <ul class="aw__reasons">
      <li v-for="(r, i) in why.unavailable.reasons" :key="i" data-test="pp-why-reason">
        <i :class="`fa ${r.icon}`" aria-hidden="true"></i>{{ r.text }}
      </li>
      <li v-if="!why.unavailable.reasons.length && why.unavailable.fallback" data-test="pp-why-reason">
        <i class="fa fa-info-circle" aria-hidden="true"></i>{{ why.unavailable.fallback }}
      </li>
    </ul>
    <div v-if="why.unavailable.tip" class="aw__tip">
      <i class="fa fa-lightbulb-o" aria-hidden="true"></i><span>{{ why.unavailable.tip }}</span>
    </div>
  </div>
</template>

<script>
/**
 * Why an action in the Patient Panel's action bar is unavailable (Schedule View Phase 8).
 * Rendered inside a desktop popover and inline in the phone / tablet "Actions" menu. The content
 * comes from buildActions() in src/utils/patientPanel/actionGates.js — Patient Profile's own text.
 */
import FeatureRequestButton from '@/components/FeatureRequestButton/FeatureRequestButton.vue';
import FeatureRequestNote from '@/components/FeatureRequestButton/FeatureRequestNote.vue';

export default {
  name: 'ActionWhy',
  components: { FeatureRequestButton, FeatureRequestNote },
  props: {
    /** `{ upsell }` or `{ unavailable: { title, reasons, tip, fallback } }`, or null. */
    why: { type: Object, default: null },
    /** The action's icon, for the upsell card. */
    icon: { type: String, default: 'fa-info-circle' },
    /** The page that draws the card ("Schedule View", "Patient Profile"): it names the page in the request email. */
    surface: { type: String, default: 'Schedule View' },
  },
  computed: {
    feature() {
      return (this.why && this.why.upsell && this.why.upsell.feature) || '';
    },
    // actionGates.UPSELLS names every request "Schedule View - <action>" (the panel came first); the page
    // that really drew the card replaces that prefix, so Nilay's email says where the click happened.
    sourcePage() {
      const named = (this.why && this.why.upsell && this.why.upsell.sourcePage) || '';
      return named.replace(/^Schedule View/, this.surface);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

// Patient Profile's popover cards (PatientProfile.scss "DISABLED FEATURE POPOVER"), in the
// panel's own palette so they sit with the rest of it.
.aw {
  text-align: left;
  font-family: 'Hanken Grotesk', 'Montserrat', sans-serif;
}

.aw--upsell {
  padding: 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, $pp-bg-2 0%, $pp-surface 100%);
}

.aw__icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  background: $pp-brand-soft;
  color: $pp-brand;
  font-size: 20px;
}

.aw__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: $pp-ink;
}

.aw__desc {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: $pp-ink-3;
}

// The request button's look. The root of <FeatureRequestButton> takes this class, so the card's own
// palette styles it (it was a bare grey browser button before 2026-10-28).
.aw__cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: $pp-brand;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;

  &.is-done,
  &:disabled {
    background: $pp-brand-soft;
    color: $pp-brand;
  }
}

.aw--unavailable {
  padding: 14px 16px;
}

.aw__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: $pp-ink-2;

  i { color: $pp-attn-ink; }
}

.aw__reasons {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 7px 0;
    font-size: 13px;
    line-height: 1.4;
    color: $pp-ink-3;
    border-bottom: 1px solid $pp-line-2;

    &:last-child { border-bottom: 0; }

    i {
      flex: 0 0 auto;
      margin-top: 2px;
      color: $pp-ink-4;
    }
  }
}

.aw__tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 11px;
  border-radius: 8px;
  background: $pp-blue-bg;
  color: $pp-blue-ink;
  font-size: 12px;
  line-height: 1.4;
}
</style>
